import { Box, Link } from "@mui/material";
import { useFormik } from "formik";
import { Link as RouterLink } from "react-router-dom";
import * as Yup from "yup";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import PasswordInput from "@/components/app/PasswordInput";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import StyledFormModal from "@/components/globalModal/StyledModal";
import { PROVIDER_PATHS } from "@/constants/paths";
import {
  NOTE_PREVIEW_COSIGN_PIN_AUTOCOMPLETE,
  NOTE_PREVIEW_COSIGN_PIN_DIALOG_WIDTH,
  NOTE_PREVIEW_COSIGN_PIN_FIELD,
  NOTE_PREVIEW_COSIGN_PIN_MIN_LENGTH,
  NOTE_PREVIEW_LABELS,
  NOTE_PREVIEW_MUTED_COLOR,
} from "../note-preview-constant";
import { NotePreviewSignatureInfo } from "../note-preview-types";

export interface CosignPinDialogProps {
  open: boolean;
  noteTitle: string;
  signature: NotePreviewSignatureInfo;
  isVerifying?: boolean;
  onCancel: () => void;
  /** Resolves true when the PIN matches; false keeps the dialog open with an error. */
  onVerify: (pin: string) => Promise<boolean>;
}

const PIN_VALIDATION_SCHEMA = Yup.object({
  pin: Yup.string()
    .trim()
    .required(NOTE_PREVIEW_LABELS.cosignPinRequired)
    .min(
      NOTE_PREVIEW_COSIGN_PIN_MIN_LENGTH,
      NOTE_PREVIEW_LABELS.cosignPinTooShort
    ),
});

const CosignPinDialog = ({
  open,
  noteTitle,
  signature,
  isVerifying,
  onCancel,
  onVerify,
}: CosignPinDialogProps) => {
  const formik = useFormik({
    initialValues: { pin: "" },
    validationSchema: PIN_VALIDATION_SCHEMA,
    onSubmit: async (values, helpers) => {
      const matches = await onVerify(values.pin.trim());
      if (!matches) {
        helpers.setFieldError(
          NOTE_PREVIEW_COSIGN_PIN_FIELD,
          NOTE_PREVIEW_LABELS.cosignPinIncorrect
        );
      }
    },
  });

  const close = () => {
    formik.resetForm();
    onCancel();
  };

  return (
    <StyledFormModal
      open={open}
      onClose={close}
      dialogTitle={NOTE_PREVIEW_LABELS.cosignDialogTitle}
      dialogAction={
        <Flex justifyContent="end" gap={1}>
          <StyledButtonV2
            label={NOTE_PREVIEW_LABELS.cancel}
            variant="outlined"
            onClick={close}
            disabled={isVerifying}
          />
          <StyledButtonV2
            label={NOTE_PREVIEW_LABELS.verifyAndContinue}
            variant="contained"
            onClick={formik.submitForm}
            disabled={!formik.values.pin.trim() || isVerifying}
          />
        </Flex>
      }
      sx={{
        "& .MuiDialog-paper": {
          height: "auto",
          width: NOTE_PREVIEW_COSIGN_PIN_DIALOG_WIDTH,
        },
      }}
    >
      <Flex flexDirection="column" gap={2} py={1}>
        <Box>
          <Text fontWeight="medium">{noteTitle}</Text>
          <Text c={NOTE_PREVIEW_MUTED_COLOR}>
            {NOTE_PREVIEW_LABELS.signedByPrefix}
            {signature.signerName}
            {NOTE_PREVIEW_LABELS.signedOnSeparator}
            {signature.signedAt}
          </Text>
        </Box>
        <PasswordInput
          name={NOTE_PREVIEW_COSIGN_PIN_FIELD}
          label={NOTE_PREVIEW_LABELS.enterCosignPin}
          required
          autoFocus
          autoComplete={NOTE_PREVIEW_COSIGN_PIN_AUTOCOMPLETE}
          inputProps={{ autoComplete: NOTE_PREVIEW_COSIGN_PIN_AUTOCOMPLETE }}
          value={formik.values.pin}
          onBlur={formik.handleBlur}
          onChange={(value) =>
            formik.setFieldValue(NOTE_PREVIEW_COSIGN_PIN_FIELD, value)
          }
          error={formik.touched.pin && formik.errors.pin}
        />
        <Link
          component={RouterLink}
          to={PROVIDER_PATHS.ACCOUNT_SETTINGS}
          underline="hover"
          alignSelf="flex-start"
        >
          {NOTE_PREVIEW_LABELS.forgotPin}
        </Link>
      </Flex>
    </StyledFormModal>
  );
};

export default CosignPinDialog;
